import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { liberalPrefixOptions } from './Prefix/utils';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import Button from '../../components/Button';
import DropDownSelect from '../DropDownSelect';
import InputGroup from '../InputGroup';
import PrefixPopup from './Prefix/PrefixPopup';
import ReactGoogleAutocomplete from '../InputFields/GoogleMapsAddressField';
import TwoGroup from '../../components/TwoGroup';

import GetUpdatesCheckbox from '../Widget/GetUpdatesCheckbox';
import LegalInfo from '../Widget/LegalInfo';

import { requiredFormElements } from './representatives';
import { isPortrait } from '../../utils/size';
import { getCachedFullName } from '../../utils/formCaching/helpers';

import { loadLocalRepSecondaryAddressCheck } from '../../containers/LocalRep/actions';

import { copyFor, capitalization } from '../../copy/copy';

import { validateStep2 } from './validations';

const DetailsFormContainer = styled.form`
    flex: 1;
    padding: 0;
    display: flex;
    flex-direction: column;
`;

const Row = styled.div`
    margin: 0 -0.5em;
    transition: opacity 0.6s;

    overflow: hidden;
    max-height: 81%;
    overflow-y: scroll;

    .message-response {
        bottom: 80px;
    }

    ${() =>
        !isPortrait() &&
        `
            &::-webkit-scrollbar {
                width: 4px;
                border-radius: 2px;
            }

            &::-webkit-scrollbar-thumb {
                border-radius: 2px;
                background: #ff245e;
            }`};

    .label-response {
        @media (min-width: 30em) {
            position: absolute;
            width: 100%;
            bottom: 142px;
            transition: bottom 0.6s;
        }
    }

    &.focused {
        &.row-wrapper-3 {
            opacity: 1;

            .label-response {
                bottom: calc(100% - 16px);
            }

            textarea {
                height: 200px;
            }
        }
    }
`;

const DynamicEmailRow = styled.div`
    width: ${() => (isPortrait() ? 100 : 50)}%;
    padding: 0 0.5em;
    float: left;
    position: relative;
`;

const HalfRow = styled.div`
    width: 50%;
    padding: 0 0.5em;
    float: left;
    position: relative;
    &:last-child {
        width: 100%;
    }
`;

const QuarterRow = styled.div`
    width: 25%;
    padding: 0 0.5em;
    float: left;
    position: relative;
`;

const ThreeQuarterRow = styled.div`
    width: 75%;
    padding: 0 0.5em;
    float: left;
    position: relative;
`;

const DynamicNameRow = styled(ThreeQuarterRow)`
    ${props => !props.includesPrefix && `width: 100%`};
`;

const ButtonGroup = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    position: absolute;
    right: 0;
    bottom: 10px;
    width: 70%;

    @media (max-width: 30em) {
        width: 100%;
    }

    > div {
        position: relative;
    }

    button {
        &.submitButton {
            ${props => (props.isSubmitting ? `text-indent: -99999px;` : '')};
            margin-left: 0.5em;

            @media (max-width: 30em) {
                width: calc(50% - 0.5em);
            }
        }

        &.middleButton {
            @media (max-width: 30em) {
                width: calc(50% - 0.5em);
                margin-right: 0.5em;
            }
        }

        &:focus {
            outline: none;
        }

        + div {
            position: absolute;
            display: none;
            top: 7px;
            right: 24%;
            margin-right: -16px;
            width: 32px !important;
            height: 32px !important;
            ${props => (props.isSubmitting ? `display: block` : '')};
        }
    }
`;

const LegalInfoWrapper = styled.div`
    position: absolute;
    left: 0;
    bottom: 10px;
    width: 30%;
    padding-right: 10px;

    @media (max-width: 30em) {
        width: 100%;
        bottom: 70px;
        padding-right: 0;
    }
`;

const FormGroupWrapper = styled.div`
    @media (max-width: 30em) {
        flex: 1;
        display: flex;
        flex-direction: row;
        margin: 0 -0.5em;

        > div {
            flex: 1;
            display: flex;
            flex-direction: column;
            margin: 0 0.5em;
            padding-top: 0px;
        }
    }
`;

class DetailsFormMultiRepStep2 extends React.Component {
    constructor() {
        super();
        this.state = {
            valid: false
        };
    }

    setValidState(bool) {
        this.setState({ valid: bool });
    }

    render() {
        return (
            <DetailsFormContainer onSubmit={this.props.handleSubmit} autocomplete="on">
                <Row>
                    {this.props.requiredFormElements
                        .filter(a => a.name === 'NamePrefix')
                        .map(a => (
                            <QuarterRow key={a.index}>
                                <Field
                                    name="prefix"
                                    component={
                                        a.options.length > 0 ? DropDownSelect : InputGroup
                                    }
                                    options={liberalPrefixOptions(a.options)}
                                    defaultOption={a.defaultOption}
                                    type="text"
                                    label={a.placeholder}
                                    placeholder={a.placeholder}
                                    onChange={e => {
                                        if (e.target && e.target.value) {
                                            if (
                                                a.options.indexOf(e.target.value) === -1
                                            ) {
                                                this.props.setPrefixModalVisibility(true);
                                            }
                                        }
                                    }}
                                />
                            </QuarterRow>
                        ))}
                    <DynamicNameRow includesPrefix={this.props.includesPrefix}>
                        <Field
                            className={'fs-block'}
                            name="fullName"
                            component={InputGroup}
                            type="text"
                            label={copyFor(
                                'formFields',
                                'fullName',
                                'email',
                                capitalization.eachWord
                            )}
                            placeholder={copyFor('placeholders', 'fullName', 'email')}
                        />
                    </DynamicNameRow>
                    <DynamicEmailRow>
                        <Field
                            className={'fs-block'}
                            name="email"
                            component={InputGroup}
                            type="email"
                            label={copyFor(
                                'formFields',
                                'emailAddress',
                                'email',
                                capitalization.eachWord
                            )}
                            placeholder={copyFor('placeholders', 'emailAddress', 'email')}
                        />
                    </DynamicEmailRow>
                    {this.props.requiredFormElements
                        .filter(a => a.name !== 'NamePrefix' && a.name !== 'Topic')
                        .sort((a, b) => a.index - b.index)
                        .map((a, index) => {
                            if (a.type === 'Address') {
                                return (
                                    <DynamicEmailRow key={a.index}>
                                        <ReactGoogleAutocomplete
                                            onPlaceSelectedCallback={address =>
                                                this.props.loadLocalRepSecondaryAddressCheck(
                                                    address.formatted_address,
                                                    this.props.representativeSearch,
                                                    this.props.maximumRepresentativeSearch, //eslint-disable-line
                                                    this.props.presetRepData
                                                )
                                            }
                                            formName={[
                                                'emailDetailsFormMultiRepStep2',
                                                'google_autocomplete'
                                            ]}
                                            fields={[
                                                'address_components',
                                                'formatted_address'
                                            ]}
                                            types={['address']}
                                            componentRestrictions={{ country: 'US' }}
                                            setValidState={this.setValidState.bind(this)}
                                            typeToReturn={'object'}
                                        />
                                    </DynamicEmailRow>
                                );
                            } else {
                                return (
                                    <HalfRow
                                        key={a.index}
                                        allVisible={
                                            this.props.requiredFormElements.length === 7
                                        }
                                    >
                                        <Field
                                            className={'fs-block'}
                                            name={a.name}
                                            key={index}
                                            component={
                                                a.options.length > 0
                                                    ? DropDownSelect
                                                    : InputGroup
                                            }
                                            options={a.options}
                                            defaultOption={a.defaultOption}
                                            type="text"
                                            label={a.placeholder}
                                            placeholder={a.placeholder}
                                        />
                                    </HalfRow>
                                );
                            }
                        })}
                </Row>
                <LegalInfoWrapper>
                    <FormGroupWrapper>
                        <GetUpdatesCheckbox
                            style={{
                                paddingBottom:
                                    this.props.endUserLocation.isEU && !isPortrait()
                                        ? 0
                                        : 12
                            }}
                            isEU={this.props.endUserLocation.isEU}
                            defaultChecked={!this.props.endUserLocation.isEU}
                            labelText={this.props.getUpdatesText}
                        />
                        <LegalInfo align="mobile-right" />
                    </FormGroupWrapper>
                </LegalInfoWrapper>
                <ButtonGroup
                    isSubmitting={
                        this.props.isSubmitting || this.props.isRepDoubleCheckLoading
                    }
                >
                    <TwoGroup>
                        <Button
                            label={copyFor(
                                'buttons',
                                'back',
                                'email',
                                capitalization.eachWord
                            )}
                            onClick={this.props.onBackFromStep2}
                            className="middleButton"
                        />
                        <Button
                            primary={true}
                            label={copyFor(
                                'buttons',
                                'send',
                                'email',
                                capitalization.eachWord
                            )}
                            type="submit"
                            className="submitButton"
                            disabled={
                                this.props.invalid ||
                                !this.state.valid ||
                                this.props.isRepDoubleCheckLoading
                            }
                        />
                        <LoadingSpinner
                            color={this.props.isRepDoubleCheckLoading && `#ff245e`}
                        />
                    </TwoGroup>
                </ButtonGroup>
                {this.props.prefixModalVisible && (
                    <PrefixPopup
                        updatePrefix={val => {
                            this.props.change('prefix', val);
                            this.props.setPrefixModalVisibility(false);
                        }}
                    />
                )}
            </DetailsFormContainer>
        );
    }
}

DetailsFormMultiRepStep2.propTypes = {
    change: PropTypes.func.isRequired,
    getUpdatesText: PropTypes.string,
    endUserLocation: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    includesPrefix: PropTypes.bool.isRequired,
    isRepDoubleCheckLoading: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    loadLocalRepSecondaryAddressCheck: PropTypes.func.isRequired,
    onBackFromStep2: PropTypes.func.isRequired,
    prefixModalVisible: PropTypes.bool,
    presetRepData: PropTypes.arrayOf(PropTypes.object),
    representativeSearch: PropTypes.shape({
        id: PropTypes.number.isRequired,
        levels: PropTypes.array.isRequired,
        roles: PropTypes.array.isRequired
    }),
    maximumRepresentativeSearch: PropTypes.shape({
        levels: PropTypes.array.isRequired,
        roles: PropTypes.array.isRequired
    }),
    requiredFormElements: PropTypes.array.isRequired,
    setPrefixModalVisibility: PropTypes.func.isRequired
};

export default compose(
    connect(
        state => {
            const formElements = requiredFormElements(state);
            return {
                requiredFormElements: formElements,
                isRepDoubleCheckLoading: state.localRep.isRepDoubleCheckLoading,
                prefixModalVisible: state.email.prefixModalVisible,
                presetRepData: state.localRep.localRepData.representative || [{}],
                representativeSearch: state.widget.activeAction.representativeSearch,
                maximumRepresentativeSearch: state.widget.maximumRepresentativeSearch,
                initialValues: {
                    prefix: state.cachedFormData.prefix || '',
                    fullName: getCachedFullName(state),
                    email: state.cachedFormData.email || '',
                    getUpdates: !(state.widget.endUserLocation || {}).isEU
                },
                includesPrefix: !!formElements.find(a => a.name === 'NamePrefix'),
                localRepFullName:
                    state.localRep.localRepData.representative !== undefined
                        ? state.localRep.localRepData.representative[0].fullName
                        : '',
                getUpdatesText: state.widget.activeAction.subscriptionOptInText,
                endUserLocation: state.widget.endUserLocation || {}
            };
        },
        {
            loadLocalRepSecondaryAddressCheck
        }
    ),
    reduxForm({
        form: 'emailDetailsFormMultiRepStep2',
        validate: validateStep2,
        destroyOnUnmount: false
    })
)(DetailsFormMultiRepStep2);
